/**
 * The reviewer's desk: the ledger, plus what the public view withholds.
 *
 * The gap it closes was measured. `recheck-exclusions` writes a finding onto
 * every exclusion it examines, including the ones it could not resolve — whose
 * finding asks an operator to curate the name — and the public ledger card
 * renders the reason, the status, the name, the rationale, the trigger and the
 * date. Not the finding. So the one actionable thing the collector produces
 * reaches nobody at all.
 *
 * Deliberately thin. What a finding is, how it is stored and what its absence
 * means are decided in `domain/lead_signals.py` and served as a sentence; this
 * renders a string or renders that there is none. A browser holding that
 * vocabulary would be holding a second copy of it, in another language, checked
 * by nothing.
 *
 * Capability gating decides what is *offered*. Every route refuses on its own
 * reading of the same principal, so hiding a control here is a courtesy and
 * never a way of keeping anybody out.
 */
import { useState } from 'react';
import { ExcludeForm } from '../components/ExcludeForm';
import { PromoteForm } from '../components/PromoteForm';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel';
import { PageHeader } from '../components/PageHeader';
import { Pill } from '../components/Pill';
import { useCapabilities } from '../hooks/useCapabilities';
import { useReopenLead } from '../hooks/useDecision';
import { useExclusions, useResearch } from '../hooks/useRadarQuery';
import { useCredential } from '../lib/credential';
import type { ResearchCandidate } from '../lib/types';

function SignIn() {
  const { setKey } = useCredential();
  const [typed, setTyped] = useState('');
  return (
    <section className="panel section-gap">
      <div className="panel__heading">
        <div>
          <span className="eyebrow">Reviewer access</span>
          <h2>Sign in to review</h2>
        </div>
      </div>
      <form
        className="filter-bar"
        onSubmit={(submitted) => {
          submitted.preventDefault();
          if (typed) setKey(typed);
        }}
      >
        <label>
          Reviewer key
          <input
            type="password"
            value={typed}
            autoComplete="off"
            onChange={(changed) => setTyped(changed.target.value)}
          />
        </label>
        <button className="button" type="submit">Sign in</button>
      </form>
      {/* Stated rather than left to be discovered. The key is held for the tab
          and nowhere else, so a reload signs the reviewer out — that is the
          cost of never writing it to storage a script can read. */}
      <p className="muted">
        Held in memory for this tab only. Reloading the page signs you out.
      </p>
    </section>
  );
}

/**
 * The decisions available on one lead.
 *
 * Which statuses may be decided is settled in Python and refused there, so
 * offering a control the service would reject is not a hole — it is a button
 * that answers `409`, which reads to a reviewer as the system being broken
 * rather than as the lead being closed. Mirrored here for that reason alone.
 */
function LeadRow({ lead }: { lead: ResearchCandidate }) {
  const reopen = useReopenLead();
  return (
    <article className="event-row" key={lead.id}>
      <div className="event-row__marks">
        <Pill value={lead.status} tone={lead.status === 'excluded' ? 'neutral' : 'accent'} />
      </div>
      <div className="event-row__body">
        <strong>{lead.nameHint}</strong>
        <span>{lead.thesis}</span>
        <code>{lead.id}</code>
        {reopen.error ? <small className="refusal">{reopen.error.message}</small> : null}
      </div>
      {lead.status === 'excluded' ? (
        <button
          className="button"
          type="button"
          disabled={reopen.isPending}
          onClick={() => reopen.mutate(lead.id)}
        >
          Reopen
        </button>
      ) : (
        /* The two decisions an open lead can take, and the mirror of the
           reopening: a decided lead answers 409 to either. Which statuses are
           open is settled and refused in Python; mirrored here so the desk
           offers what the service would accept rather than buttons that report
           a fault. Promotion is the heavier of the two and sits second. */
        <div className="decision-pair">
          <ExcludeForm candidateId={lead.id} nameHint={lead.nameHint} />
          <PromoteForm candidateId={lead.id} nameHint={lead.nameHint} />
        </div>
      )}
    </article>
  );
}

export function DeskPage() {
  const { may, isLoading: resolving } = useCapabilities();
  const ledger = useExclusions([]);
  const queue = useResearch();

  if (resolving) return <LoadingPanel label="Checking what you may do…" />;
  if (!may('decide_a_lead')) {
    return (
      <>
        <PageHeader
          eyebrow="Review desk"
          title="Reviewer desk"
          description="Decisions the registry has recorded, with what re-checking them last found."
        />
        <SignIn />
      </>
    );
  }
  if (ledger.isError) return <ErrorPanel error={ledger.error} />;
  if (ledger.isLoading || !ledger.data) return <LoadingPanel label="Loading the ledger…" />;

  return (
    <>
      <PageHeader
        eyebrow="Review desk"
        title="Reviewer desk"
        description="Decisions the registry has recorded, with what re-checking them last found."
      />
      <section className="panel section-gap">
        <div className="panel__heading">
          <div><span className="eyebrow">Decide</span><h2>Research queue</h2></div>
        </div>
        {queue.data?.items.length
          ? queue.data.items.map((lead) => <LeadRow key={lead.id} lead={lead} />)
          : <EmptyPanel message="No leads are open." />}
      </section>
      <section className="panel section-gap">
        <div className="panel__heading">
          <div><span className="eyebrow">Considered and ruled out</span><h2>Exclusion ledger</h2></div>
        </div>
        {ledger.data.items.length ? ledger.data.items.map((entry) => (
          <article className="event-row" key={entry.id}>
            <div className="event-row__marks">
              <Pill value={entry.reasonCode} tone="accent" />
              <Pill
                value={entry.status}
                tone={entry.status === 'excluded' ? 'neutral' : 'warning'}
              />
            </div>
            <div className="event-row__body">
              <strong>{entry.nameHint}</strong>
              <span>{entry.rationale}</span>
              <small className="muted">Reopens if: {entry.reconsiderationTrigger}</small>
              {/* The finding, or the absence of one. Two different facts, and
                  the whole reason a null is written as deliberately as a hit —
                  carried by the sentence, since neither is a problem to flag:
                  the pass runs on a schedule, so an entry it has not reached
                  yet is simply newer than the last run. */}
              <small className="muted">
                {entry.lastRecheck ?? 'Never re-checked against a register.'}
              </small>
              {/* The identifier every decision command addresses. Without it an
                  operator reads the queue here and looks it up somewhere else. */}
              <code>{entry.id}</code>
            </div>
            <time>{entry.reviewedAt.slice(0, 10)}</time>
          </article>
        )) : <EmptyPanel message="Nothing has been ruled out yet." />}
      </section>
    </>
  );
}
